/**
 * The AI vocabulary in words (client-MCP plan §12.5; contract `../apiv2/dashboard_ai_edit.openapi.yaml`
 * `/ai_vocabulary/`): where an entry comes from, what its binding means, what an edit changes (`VocabularyRecord`),
 * where a proposed edit went (`route_reason`) and why one was refused. Shared by the AI vocabulary page and the
 * approvals inbox, which shows a vocabulary change's records on its card (a vocabulary approver may not open the
 * notification proposal review).
 *
 * Terms, other words, definitions and the names a binding points at are the company's own text: they render through
 * `components/UntrustedText` (plain text, never markup), apart from our own words.
 */
import { Alert, Box, Stack, Typography } from '@mui/material';
import * as React from 'react';

import { UntrustedText } from '../../components/UntrustedText';
import { MCP_PERMISSIONS } from '../../users/agroups/mcpPermissions';

export const VOCABULARY_WRITE = MCP_PERMISSIONS['vocabulary.mcp_write'];
export const VOCABULARY_APPROVE = MCP_PERMISSIONS['vocabulary.mcp_approve'];
export const PAGE_PATH = '/settings/ai-vocabulary';

/** `origin`: who wrote the entry. */
export const ORIGIN_LABEL = { tenant: 'Your company', ops: 'Curated Planet support', auto: 'From your set-up' };
export const originLabel = (origin) => ORIGIN_LABEL[origin] || origin;

/** An edit's action, before the term it acts on. */
export const ACTION_WORDS = { create: 'New entry', update: 'Change of', delete: 'Removal of' };

/** The channels a binding may name (backend `tenant_terms.CHANNEL_NAMES`). */
export const CHANNEL_NAMES = {
    email: 'Email',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    chat: 'App chat',
    push: 'Push notification',
    airbnb: 'Airbnb message',
    http: 'HTTP request',
    sns: 'SNS topic',
    sentry: 'Sentry',
};

const named = (ids, names) => (Array.isArray(ids) ? ids : []).map((id) => names?.find((n) => n.id === id)?.name || `#${id}`);

/**
 * A binding as lines of text ("Regions: South America, Central America"): `names` is the answer's `binding_names`
 * (the company's own rows; an id no longer its own reads as "#id"). An unknown key reads as itself.
 */
export const bindingLines = (binding, names = {}) => {
    if (!binding || typeof binding !== 'object') return [];
    const lines = [];
    Object.entries(binding).forEach(([key, value]) => {
        switch (key) {
            case 'role_ids':
                lines.push(`Staff roles: ${named(value, names.role_ids).join(', ')}`);
                break;
            case 'region_ids':
                lines.push(`Regions: ${named(value, names.region_ids).join(', ')}`);
                break;
            case 'countries':
                lines.push(`Countries: ${(Array.isArray(value) ? value : [value]).join(', ')}`);
                break;
            case 'tag_category':
                lines.push(`Tag category: ${value}`);
                break;
            case 'booking_system':
                lines.push(`Booking system: ${value}`);
                break;
            case 'channel':
                lines.push(`Channel: ${CHANNEL_NAMES[value] || value}`);
                break;
            case 'language':
                lines.push(`Language: ${value}${binding.default ? ' (the default)' : ''}`);
                break;
            case 'default':
                break; // with the language
            default:
                lines.push(`${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`);
        }
    });
    return lines;
};

/** The binding's lines, each one value of the company's own text. */
export const BindingText = ({ binding, names, empty = '—' }) => {
    const lines = bindingLines(binding, names);
    if (!lines.length) return empty ? <Typography variant="body2" color="text.secondary">{empty}</Typography> : null;
    return lines.map((line) => (
        <Typography key={line} variant="body2" sx={{ wordBreak: 'break-word' }}>{line}</Typography>
    ));
};

const FIELD_LABEL = { term: 'Term', aliases: 'Other words', concept: 'Stands for', binding: 'Means', definition: 'Definition' };

const valueText = (field, value, names) => {
    if (value === null || value === undefined || value === '') return '';
    if (field === 'binding') return bindingLines(value, names).join('; ');
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
};

/** One entry as an edit leaves it (`VocabularyEntrySnapshot`). */
const Snapshot = ({ entry }) => (
    <Box sx={{ pl: 2 }}>
        {entry.aliases?.length > 0 && <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{`Also: ${entry.aliases.join(', ')}`}</Typography>}
        <Typography variant="body2">Stands for: {entry.concept_name || entry.concept}</Typography>
        <BindingText binding={entry.binding} names={entry.binding_names} empty="" />
        {entry.definition && <UntrustedText text={entry.definition} label="Definition" />}
    </Box>
);

const ChangeRow = ({ field, change, names }) => (
    <Box sx={{ pl: 2, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" component="div">{FIELD_LABEL[field] || field}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'baseline' }}>
            <UntrustedText inline text={valueText(field, change?.before, null)} empty="(none)" />
            <Typography variant="body2" component="span" aria-hidden>→</Typography>
            <UntrustedText inline text={valueText(field, change?.after, names)} empty="(none)" />
        </Stack>
    </Box>
);

const recordTerm = (record) => record.term || record.after?.term || record.before?.term;

/** What an edit changes, entry by entry (`VocabularyRecord`s: the backend's preview, or a pending change's summary). */
export const VocabularyRecords = ({ records }) => {
    const list = Array.isArray(records) ? records : [];
    if (!list.length) return null;
    return (
        <Stack spacing={1} sx={{ mt: 1 }}>
            {list.map((record) => (
                <Box key={`${record.item}-${record.op}`}>
                    <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 500 }}>{ACTION_WORDS[record.op] || record.op}</Box>
                        {' '}
                        {recordTerm(record)
                            ? <UntrustedText inline text={recordTerm(record)} />
                            : <Box component="span">{`entry #${record.entry}`}</Box>}
                    </Typography>
                    {record.op === 'create' && record.after && <Snapshot entry={record.after} />}
                    {record.op === 'update' && Object.entries(record.changes || {}).map(([field, change]) => (
                        <ChangeRow key={field} field={field} change={change} names={record.binding_names} />
                    ))}
                </Box>
            ))}
        </Stack>
    );
};

/** A preview's findings (e.g. `vocabulary.guide_role_mismatch`: reported, never acted on) and warnings. */
export const FindingsAndWarnings = ({ findings, warnings }) => (
    <>
        {(Array.isArray(findings) ? findings : []).map((f, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Alert key={`f-${i}`} severity={f.severity === 'info' ? 'info' : 'warning'} sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{f.title || f.code}</Typography>
                {f.explanation && <Typography variant="body2">{f.explanation}</Typography>}
            </Alert>
        ))}
        {(Array.isArray(warnings) ? warnings : []).map((w, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Alert key={`w-${i}`} severity="warning" sx={{ mt: 1 }}>{String(w)}</Alert>
        ))}
    </>
);

/** Where a proposed edit went (`route_reason`, client-MCP plan §10.1). `inbox`: it waits in the approvals inbox. */
export const routeText = (result, name) => {
    switch (result.route_reason) {
        case 'sandbox':
            return { severity: 'success', text: 'Applied at once: a sandbox applies every change directly.' };
        case 'auto_apply':
            return { severity: 'success', text: `Applied at once: auto-apply is on for ${name}.` };
        case 'self_approval':
            return { severity: 'success', text: 'Applied at once: you may approve vocabulary changes yourself.' };
        case 'strong_sign_in_required':
            return {
                severity: 'info',
                inbox: true,
                text: 'Waiting in AI changes › Approvals: you may approve it yourself, but this session did not sign in with '
                    + 'two-step sign-in (or single sign-on). Approve it there, or sign in again with your second factor.',
            };
        case 'approval_required':
            return { severity: 'info', inbox: true, text: `Waiting in AI changes › Approvals for someone with “${VOCABULARY_APPROVE}”.` };
        default:
            return result.applied
                ? { severity: 'success', text: 'Applied.' }
                : { severity: 'info', inbox: true, text: 'Waiting in AI changes › Approvals.' };
    }
};

/** Why a proposed edit was refused (`DashboardAIError`); nothing changed in any case. */
export const proposeErrorText = (e, name) => {
    switch (e.code) {
        case 'invalid':
        case 'invalid_change':
            return `The edit was refused: ${e.message}`;
        case 'all_tags_required':
            return `Proposing an edit needs “${VOCABULARY_WRITE}” on all tags: the vocabulary is the whole company's. Nothing changed.`;
        case 'vocabulary_write_required':
            return `Proposing an edit needs “${VOCABULARY_WRITE}”, granted explicitly. Nothing changed.`;
        case 'vocabulary_permission_required':
            return 'You no longer hold an AI vocabulary permission here. Nothing changed.';
        case 'vocabulary_disabled':
            return 'Editing the AI vocabulary is not switched on yet. Nothing changed.';
        case 'domain_disabled':
            return 'AI editing of the vocabulary is switched off for your company (by Curated Planet operations, or on your '
                + 'franchise parent). Nothing changed.';
        case 'change_sets_disabled':
            return 'AI changes cannot be applied yet on this server (change sets are off). Nothing changed.';
        case 'busy':
            return 'Another change is being applied: try again in a moment. Nothing changed.';
        case 'apply_failed':
            return `The edit did not apply: ${e.message} Nothing changed.`;
        default:
            if (e.status === 404) return `This entry no longer exists (or is not ${name}'s): reload the page. Nothing changed.`;
            return `Could not propose the edit: ${e.message}`;
    }
};
